import { useState, useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../../../services/api-service.js";
import moment from "moment";
import { ReactComponent as ModalCloseIcon } from "../../../Assets/svg/close-modal.svg";
import { Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Ticketsadd.css";
import greyIcon from "../../../Assets/svg/grey.svg";
import yellowIcon from "../../../Assets/svg/yellow.svg";
import { getEnabledFieldApi } from "../Ticketapi.js";
import PropTypes from "prop-types";

const TicketDateInput = forwardRef(function TicketDateInput({ value, onClick, placeholder }, ref) {
  return (
    <input
      ref={ref}
      value={value}
      onClick={onClick}
      readOnly
      className="form-control w-100"
      style={{
        height: "40px",
        borderRadius: "10px",
        border: "0.7px solid #E5E5E5",
        padding: "16px",
        cursor: "pointer",
      }}
      placeholder={placeholder}
    />
  );
});

const StatusUpdateModal = ({ ticketDetail, statusUpdate, success, onHide }) => {
  function formatToDateOnly(dateString) {
    // Convert "18 Sep 2025 at 02:30 PM" → Date object
    const date = new Date(dateString.replace(" at", ""));

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      padding: "20px 24px",
      borderRadius: "16px",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
      width: "520px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      textAlign: "center",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    },
  };

  const [enabledFields, setEnabledFields] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});
  const getEnabledField = async () => {
    try {
      const response = await getEnabledFieldApi();
      if (response.success === true) {
        setEnabledFields(response?.enabled_fields ?? []);
        setMandatoryFields(response?.mandatory_fields ?? []);
        setFieldLabels(response?.application_labels ?? response?.labels ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    }
  };
  const isFieldEnabled = (field) => {
    if (field === "Title" || field === "Project") return true;
    if (field === "Customer" || field === "Client Name") {
      return enabledFields.some((item) => item === "Customer" || item === "Client Name");
    }
    return enabledFields.some((item) => item === field);
  };
  const isFieldMandatory = (field) => {
    if (field === "Title" || field === "Project") return true;
    if (!isFieldEnabled(field)) return false;
    if (field === "Customer" || field === "Client Name") {
      return mandatoryFields.some((item) => item === "Customer" || item === "Client Name");
    }
    return mandatoryFields.some((item) => item === field);
  };
  const getFieldLabel = (fieldKey, defaultLabel = fieldKey) => {
    let custom = fieldLabels?.[fieldKey];
    if ((!custom || typeof custom !== "string" || custom.trim() === "") && (fieldKey === "Customer" || fieldKey === "Client Name")) {
      custom = fieldLabels?.["Customer"] || fieldLabels?.["Client Name"];
    }
    if (custom && typeof custom === "string" && custom.trim() !== "") {
      return custom.trim();
    }
    return defaultLabel;
  };
  const renderFieldLabel = (fieldKey, defaultLabel, isMandatoryExplicit = null) => {
    const labelText = getFieldLabel(fieldKey, defaultLabel);
    const isMandatory = isMandatoryExplicit !== null ? isMandatoryExplicit : isFieldMandatory(fieldKey);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          verticalAlign: "middle",
        }}
      >
        <span
          title={labelText}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {labelText}
        </span>
        {isMandatory && <span style={{ color: "red", marginLeft: "3px", flexShrink: 0 }}> *</span>}
      </span>
    );
  };

  const [descriptionInputValue, setDescriptionInputValue] = useState("");
  const [actionTaken, setActionTaken] = useState(ticketDetail?.action_taken || "");
  const [expectedDate, setExpectedDate] = useState(moment(new Date()).format("YYYY-MM-DDTHH:mm"));
  const [closingDate, setClosingDate] = useState(moment(new Date()).format("YYYY-MM-DDTHH:mm"));
  const [workDuration, setWorkDuration] = useState("");
  const [lastKey, setLastKey] = useState("");
  const [rating, setRating] = useState(0);
  const [isCloseHovered, setIsCloseHovered] = useState(false);

  const parseTicketDate = (value) => {
    if (!value) {
      return null;
    }

    const parsed = moment(value, ["YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD", "DD-MM-YYYY HH:mm", "DD-MM-YYYY"], true);

    if (parsed.isValid()) {
      return parsed;
    }

    const fallback = moment(value);
    return fallback.isValid() ? fallback : null;
  };
  function getToday() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // Usage
  useEffect(() => {
    getEnabledField();
  }, []);

  useEffect(() => {
    if (ticketDetail?.action_taken !== undefined && ticketDetail?.action_taken !== null) {
      setActionTaken(ticketDetail.action_taken);
    }
  }, [ticketDetail]);

  useEffect(() => {
    if (statusUpdate === "Closed" && (!workDuration || workDuration === "00:00")) {
      const rawDuration = ticketDetail?.duration || ticketDetail?.syslogged_duration || ticketDetail?.total_duration || ticketDetail?.est;
      if (rawDuration && rawDuration !== "--:--") {
        const timeStr = String(rawDuration).split(".")[0];
        const parts = timeStr.split(":");

        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        if (parts.length >= 2) {
          hours = parseInt(parts[0], 10) || 0;
          minutes = parseInt(parts[1], 10) || 0;
          seconds = parts.length === 3 ? parseInt(parts[2], 10) || 0 : 0;

          if ((ticketDetail.status === "In Progress" || ticketDetail.status === "In-Progress") && ticketDetail.fetchedAt) {
            const initialTotalSeconds = hours * 3600 + minutes * 60 + seconds;
            const secondsElapsed = Math.floor((new Date() - new Date(ticketDetail.fetchedAt)) / 1000);
            const liveTotalSeconds = initialTotalSeconds + secondsElapsed;

            hours = Math.floor(liveTotalSeconds / 3600);
            minutes = Math.floor((liveTotalSeconds % 3600) / 60);
          }
          setWorkDuration(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`);
        } else {
          setWorkDuration(timeStr);
        }
      }
    }
  }, [statusUpdate, ticketDetail, workDuration]);
  const handleDateChange = (date) => {
    console.log("handleDateChange.......", date);
    if (date !== undefined) {
      setExpectedDate(moment(date).format("YYYY-MM-DDTHH:mm"));
    }
  };
  const handleClosingDateChange = (date) => {
    if (date !== undefined) {
      console.log(moment(date).format("YYYY-MM-DDTHH:mm"));
      setClosingDate(moment(date).format("YYYY-MM-DDTHH:mm"));
    }
  };

  const handleChange = (event) => {
    const inputText = event.target.value;
    if (/^\d{0,2}(:\d{0,2})?$/.test(inputText)) {
      console.log(event.key);
      if (lastKey === "Backspace") {
        setWorkDuration(inputText);
      } else {
        setWorkDuration(inputText.length === 2 ? inputText + ":" : inputText);
      }
    }
  };

  const [validations, setValidations] = useState({
    expected_date: "",
    description: "",
    work_duration: "",
    closing_date: "",
    action_taken: "",
  });

  const statusUpdateApi = async () => {
    setValidations({
      expected_date: "",
      description: "",
      work_duration: "",
      closing_date: "",
      action_taken: "",
    });
    if (
      statusUpdate === "Closed" &&
      isFieldEnabled("Close comment") &&
      isFieldMandatory("Close comment") &&
      !descriptionInputValue.trim()
    ) {
      setValidations((prev) => ({
        ...prev,
        description: `${getFieldLabel("Close comment", "Remarks")} is required`,
      }));
      return;
    }
    if (statusUpdate === "Closed" && isFieldMandatory("Action Taken") && !actionTaken.trim()) {
      setValidations((prev) => ({
        ...prev,
        action_taken: `${getFieldLabel("Action Taken", "Action Taken")} is required`,
      }));
      return;
    }
    if (statusUpdate === "Closed" && isFieldEnabled("Work Duration") && isFieldMandatory("Work Duration") && !workDuration) {
      setValidations((prev) => ({
        ...prev,
        work_duration: `${getFieldLabel("Work Duration", "Work duration")} is required`,
      }));
      return;
    }
    if (statusUpdate === "Closed" && isFieldEnabled("Work Duration") && workDuration) {
      const [hours, minutes] = workDuration.split(":").map(Number);
      if (hours === 0 && minutes === 0) {
        setValidations((prev) => ({
          ...prev,
          work_duration: "Work duration cannot be 00:00",
        }));
        return;
      }
    }
    if (statusUpdate === "Closed" && isFieldEnabled("Closing Date") && isFieldMandatory("Closing Date") && !closingDate) {
      setValidations((prev) => ({
        ...prev,
        closing_date: `${getFieldLabel("Closing Date", "Closing date")} is required`,
      }));
      return;
    }
    if (statusUpdate === "Hold" && isFieldEnabled("Hold comment") && isFieldMandatory("Hold comment") && !descriptionInputValue.trim()) {
      setValidations((prev) => ({
        ...prev,
        description: `${getFieldLabel("Hold comment", "Remarks")} is required`,
      }));
      return;
    }
    if (statusUpdate === "Hold" && isFieldEnabled("Expected Date") && isFieldMandatory("Expected Date") && !expectedDate) {
      setValidations((prev) => ({
        ...prev,
        expected_date: `${getFieldLabel("Expected Date", "Expected date")} is required`,
      }));
      return;
    }

    const data = {
      status: statusUpdate,
    };
    if (statusUpdate === "Hold") {
      data["expected_date"] = expectedDate;
      data["description"] = descriptionInputValue?.trim() ? descriptionInputValue : "Hold";
    }
    if (statusUpdate === "Closed") {
      data["work_duration"] = workDuration;
      data["closing_date"] = closingDate;
      data["description"] = descriptionInputValue?.trim()
        ? descriptionInputValue
        : (actionTaken?.trim() || "Closed");
      data["action_taken"] = actionTaken;
    }
    if (statusUpdate === "Completed") {
      data["rating"] = rating;
      data["description"] = descriptionInputValue?.trim() ? descriptionInputValue : "Completed";
    }
    if (statusUpdate === "Reopen") {
      data["description"] = descriptionInputValue?.trim() ? descriptionInputValue : "Reopen";
    }

    setLoading(true);
    try {
      const response = await apiCall("put", `ticket/status_update/${ticketDetail.id}`, data);
      if (response?.status === 200 && response?.data?.success === true) {
        success();
      } else if (response?.status === 400 && response?.data?.error) {
        if (statusUpdate === "Closed") {
          const errors = response.data.error;
          const modalFields = ["work_duration", "closing_date", "description", "action_taken"];
          const hasOtherErrors = Object.keys(errors).some((key) => !modalFields.includes(key));

          if (hasOtherErrors) {
            navigate("/ticket/add", {
              state: { id: ticketDetail.id, errors: errors },
            });
            onHide();
            return;
          }
        }
        const validationTemp = {
          expected_date: "",
          description: "",
          work_duration: "",
          closing_date: "",
          action_taken: "",
        };
        Object.entries(response.data.error).forEach(([key, value]) => {
          validationTemp[key] = value.length === 0 ? "" : value[0];
        });
        setValidations(validationTemp);
      }
    } catch (err) {
      console.error("Status Update Error:", err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={modalStyle.overlay}>
      <div style={modalStyle.modal}>
        <div style={{ color: "#ffffff" }}>
          <div
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "500",
                  color: "#212129",
                }}
              >
                {modalTitle(statusUpdate)}
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "400",
                  color: "#98A1B0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "450px",
                }}
                title={ticketDetail?.title || ""}
              >
                {ticketDetail?.title ?? ""}
              </div>
            </div>

            <div
              style={{
                cursor: "pointer",
                padding: "8px",
                borderRadius: "50%",
                backgroundColor: isCloseHovered ? "#F5F5F5" : "transparent",
                transition: "background-color 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "-0px",
                marginRight: "-8px",
              }}
              onMouseEnter={() => setIsCloseHovered(true)}
              onMouseLeave={() => setIsCloseHovered(false)}
              onClick={onHide}
            >
              <ModalCloseIcon />
            </div>
          </div>

          <div style={{ marginTop: "8px" }}>
            {statusUpdate === "Hold" && isFieldEnabled("Expected Date") && (
              <div>
                <div
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#212129",
                    maxWidth: "100%",
                  }}
                >
                  {renderFieldLabel("Expected Date", "Expected Date")}
                </div>
                <DatePicker selected={expectedDate ? (parseTicketDate(expectedDate)?.toDate() ?? null) : null} onChange={handleDateChange} showTimeInput timeInputLabel="Enter Time:" dateFormat="dd-MM-yyyy hh:mm aa" minDate={new Date()} placeholderText="dd-mm-yyyy hh:mm am/pm" wrapperClassName="w-100" popperClassName="expected-date-popper" portalId="portal-root" popperPlacement="bottom-start" popperProps={{ strategy: "fixed" }} customInput={<TicketDateInput />} />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#FF4444",
                  }}
                >
                  {validations.expected_date}
                </div>
              </div>
            )}
            {statusUpdate === "Closed" && (
              <Row>
                {isFieldEnabled("Work Duration") && (
                  <Col sm={6}>
                    <div
                      style={{
                        paddingTop: "6px",
                        paddingBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "start",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#212129",
                        maxWidth: "100%",
                      }}
                    >
                      {renderFieldLabel("Work Duration", "Work Duration")}
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={workDuration}
                      onChange={handleChange}
                      onKeyDown={(event) => {
                        setLastKey(event.key);
                      }}
                      placeholder="HH:MM"
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#FF4444",
                      }}
                    >
                      {validations.work_duration}
                    </span>
                  </Col>
                )}
                {isFieldEnabled("Closing Date") && (
                  <Col sm={6}>
                    <div
                      style={{
                        paddingTop: "6px",
                        paddingBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "start",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#212129",
                        maxWidth: "100%",
                      }}
                    >
                      {renderFieldLabel("Closing Date", "Closing Date")}
                    </div>
                    <DatePicker selected={closingDate ? (parseTicketDate(closingDate)?.toDate() ?? null) : null} onChange={handleClosingDateChange} showTimeInput timeInputLabel="Enter Time:" dateFormat="dd-MM-yyyy hh:mm aa" minDate={parseTicketDate(formatToDateOnly(ticketDetail.start_date ?? ""))?.toDate() ?? null} maxDate={new Date()} placeholderText="dd-mm-yyyy hh:mm am/pm" wrapperClassName="w-100" popperClassName="expected-date-popper" portalId="portal-root" popperPlacement="bottom-start" popperProps={{ strategy: "fixed" }} customInput={<TicketDateInput />} />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#FF4444",
                      }}
                    >
                      {validations.closing_date}
                    </span>
                  </Col>
                )}
              </Row>
            )}
            {statusUpdate === "Completed" && (
              <div>
                <div
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6x",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#212129",
                  }}
                >
                  Rating
                </div>
                <div style={{ display: "flex", justifyContent: "start" }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} onClick={() => setRating(star)} style={{ cursor: "pointer", fontSize: "24px" }}>
                      {star <= rating ? <img src={yellowIcon} alt="filled star" /> : <img src={greyIcon} alt="empty star" />}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {statusUpdate === "Closed" && isFieldEnabled("Action Taken") && (
              <div>
                <div>
                  <div
                    style={{
                      paddingTop: "6px",
                      paddingBottom: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#212129",
                      maxWidth: "100%",
                    }}
                  >
                    {renderFieldLabel("Action Taken", "Action Taken")}
                  </div>
                  <textarea
                    type="text"
                    placeholder=" "
                    className="form-control mt-2"
                    value={actionTaken}
                    rows={3}
                    onChange={(event) => {
                      setActionTaken(event.target.value);
                    }}
                  />
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                    }}
                  >
                    {validations.action_taken}
                  </span>
                </div>
              </div>
            )}
            {((statusUpdate === "Hold" && isFieldEnabled("Hold comment")) || (statusUpdate === "Closed" && isFieldEnabled("Close comment")) || statusUpdate === "Reopen" || (statusUpdate === "Completed" && isFieldEnabled("Rating comment"))) && (
              <div>
                <div>
                  <div
                    style={{
                      paddingTop: "6px",
                      paddingBottom: "6x",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#212129",
                      maxWidth: "100%",
                    }}
                  >
                    {(() => {
                      const remarksField = statusUpdate === "Hold" ? "Hold comment" : statusUpdate === "Closed" ? "Close comment" : statusUpdate === "Completed" ? "Rating comment" : "Remarks";
                      const isMandatory = statusUpdate === "Hold"
                        ? isFieldMandatory("Hold comment")
                        : statusUpdate === "Closed"
                        ? isFieldMandatory("Close comment")
                        : statusUpdate === "Completed"
                        ? isFieldMandatory("Rating comment")
                        : statusUpdate === "Reopen";
                      return renderFieldLabel(remarksField, "Remarks", isMandatory);
                    })()}
                  </div>
                  <textarea
                    type="text"
                    placeholder=" "
                    className="form-control mt-2"
                    value={descriptionInputValue}
                    rows={3}
                    onChange={(event) => {
                      setDescriptionInputValue(event.target.value); // Update state with input value
                    }}
                  />
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                    }}
                  >
                    {validations.description}
                  </span>
                </div>
              </div>
            )}
            {statusUpdate === "Closed" && ticketDetail.sub_ticket_status !== "Closed" && (
              <div className="style-justify-start mt-2">
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#FF4444",
                  }}
                >
                  {ticketDetail.sub_ticket_confirmation ?? ""}
                </span>
              </div>
            )}
            <Row
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                marginTop: "16px",
              }}
            >
              <div
                onClick={onHide}
                style={{
                  cursor: "pointer",
                  width: "176px",
                  height: "44px",
                  marginLeft: "8px",
                  marginRight: "8px",
                  borderRadius: "100px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#FFFFFF",
                  border: "0.6px solid #E5E5E5",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#1E1E1E",
                  }}
                >
                  Cancel
                </div>
              </div>
              <div
                onClick={(e) => {
                  if (loading === false) {
                    statusUpdateApi();
                  }
                }}
                style={{
                  cursor: "pointer",
                  width: "176px",
                  height: "44px",
                  marginLeft: "8px",
                  marginRight: "8px",
                  borderRadius: "100px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#3253EB",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#FFFFFF",
                  }}
                >
                  Submit
                </div>
              </div>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

function modalTitle(updateStatus) {
  if (updateStatus === "Hold") {
    return "Holding remarks";
  } else if (updateStatus === "Closed") {
    return "Closing remarks";
  } else if (updateStatus === "Completed") {
    return "Completing remarks";
  } else if (updateStatus === "Reopen") {
    return "Reopen remarks";
  } else {
    return "";
  }
}

StatusUpdateModal.propTypes = {
  ticketDetail: PropTypes.object,
  statusUpdate: PropTypes.string,
  success: PropTypes.func,
  onHide: PropTypes.func,
};

export default StatusUpdateModal;
